import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AudioLines,
  Podcast,
  ScanText,
  BotMessageSquare,
  Brain,
  FileInput,
  Upload,
  Sparkles,
  Download,
  ArrowRight,
  Menu,
  X,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useTenantConfig } from '../../contexts/TenantConfigContext';

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

enum NavSection {
  FEATURES = 'features',
  HOW_IT_WORKS = 'how-it-works',
}

const NAV_LINKS = [
  { label: 'Features', section: NavSection.FEATURES },
  { label: 'How It Works', section: NavSection.HOW_IT_WORKS },
];

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
}

const FEATURES: Feature[] = [
  {
    icon: AudioLines,
    title: 'Audio Transcription',
    description:
      'Upload recordings and get polished transcripts with optional speaker labels and AI correction.',
  },
  {
    icon: Podcast,
    title: 'Live Recording',
    description:
      'Record directly in-browser for up to 90 minutes with real-time processing and diarization.',
  },
  {
    icon: ScanText,
    title: 'Document Extraction',
    description:
      'Extract text from PDFs and DOCX files, including scanned documents via OCR.',
  },
  {
    icon: BotMessageSquare,
    title: 'Document Chat',
    description:
      'Chat with your documents — ask questions and get AI-grounded answers instantly.',
  },
  {
    icon: Brain,
    title: 'Q&A Assist',
    description:
      'Upload questions and reference documents, get precise AI-generated answers.',
  },
  {
    icon: FileInput,
    title: 'Form Filler',
    description:
      'Upload a form and source documents — AI fills it out for you automatically.',
  },
];

interface Step {
  icon: LucideIcon;
  title: string;
  description: string;
}

const STEPS: Step[] = [
  {
    icon: Upload,
    title: 'Upload',
    description: 'Drop your audio files, documents, or paste text directly.',
  },
  {
    icon: Sparkles,
    title: 'AI Processes',
    description: 'Advanced AI extracts, transcribes, and transforms your content.',
  },
  {
    icon: Download,
    title: 'Download',
    description: 'Get polished, structured output ready to use.',
  },
];

/* Pricing tiers hidden — re-enable when pricing is finalized */

interface Stat {
  value: string;
  label: string;
}

const STATS: Stat[] = [
  { value: '10,000+', label: 'Documents Processed' },
  { value: '50,000+', label: 'Minutes Transcribed' },
  { value: '99.2%', label: 'Accuracy Rate' },
  { value: '<5s', label: 'Average Processing' },
];

const MOCK_TABS = ['Audio', 'Record', 'Document', 'Doc Chat', 'Paste', 'Q&A', 'Forms'];

/* ------------------------------------------------------------------ */
/*  Shared styles                                                      */
/* ------------------------------------------------------------------ */

const glassCardSx = {
  background: 'var(--glass)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid var(--border-hi)',
  borderRadius: 'var(--radius-lg)',
  position: 'relative' as const,
  overflow: 'hidden' as const,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
  },
};

const gradientTextSx = {
  background: 'linear-gradient(135deg, var(--teal), var(--cyan))',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const sectionLabelSx = {
  fontFamily: "'Syne', sans-serif",
  fontSize: '0.8rem',
  fontWeight: 700,
  letterSpacing: '0.3em',
  textTransform: 'uppercase' as const,
  ...gradientTextSx,
  mb: 1.5,
};

const sectionHeadingSx = {
  fontFamily: "'Syne', sans-serif",
  fontWeight: 700,
  color: 'var(--t1)',
  lineHeight: 1.2,
  letterSpacing: '-0.02em',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { config } = useTenantConfig();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 700, once: true, easing: 'ease-out-cubic', offset: 80 });
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = useCallback((id: string) => {
    setDrawerOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 10);
  }, []);

  const brandName = config?.branding?.displayName || 'AI-SCRIBE';
  const logoUrl = config?.branding?.logoUrl || '/brand/app-icon.svg';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'var(--bg)', color: 'var(--t1)', overflowX: 'hidden' }}>

      {/* ============================================================ */}
      {/*  NAVBAR                                                       */}
      {/* ============================================================ */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
          bgcolor: scrolled ? 'rgba(8,9,13,0.88)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <Box
                component="img"
                src={logoUrl}
                alt={brandName}
                sx={{ height: 32, width: 'auto', borderRadius: '6px' }}
              />
              <Typography
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  ...gradientTextSx,
                  letterSpacing: '0.15em',
                }}
              >
                {brandName}
              </Typography>
            </Box>

            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {NAV_LINKS.map((l) => (
                  <Button
                    key={l.section}
                    onClick={() => scrollTo(l.section)}
                    sx={{
                      color: 'var(--t2)',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      '&:hover': { color: 'var(--teal)', bgcolor: 'transparent' },
                    }}
                  >
                    {l.label}
                  </Button>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {!isMobile && (
                <Button
                  onClick={() => navigate('/login')}
                  sx={{
                    color: 'var(--t2)',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    '&:hover': { color: 'var(--t1)', bgcolor: 'transparent' },
                  }}
                >
                  Sign In
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => navigate('/signup')}
                sx={{
                  background: 'linear-gradient(135deg, var(--teal), var(--cyan))',
                  color: 'var(--bg)',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  px: 3,
                  py: 0.8,
                  borderRadius: 'var(--radius-sm)',
                  boxShadow: '0 4px 20px rgba(0,229,195,0.18)',
                  '&:hover': { boxShadow: '0 6px 28px rgba(0,229,195,0.28)' },
                }}
              >
                Get Started
              </Button>
              {isMobile && (
                <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: 'var(--t2)' }}>
                  <Menu size={24} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { bgcolor: 'var(--s1)', width: 280, pt: 2 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 1 }}>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'var(--t2)' }}>
            <X size={24} />
          </IconButton>
        </Box>
        <List>
          {NAV_LINKS.map((l) => (
            <ListItemButton key={l.section} onClick={() => scrollTo(l.section)}>
              <ListItemText
                primary={l.label}
                sx={{ '& .MuiListItemText-primary': { color: 'var(--t1)', fontWeight: 500 } }}
              />
            </ListItemButton>
          ))}
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/login'); }}>
            <ListItemText
              primary="Sign In"
              sx={{ '& .MuiListItemText-primary': { color: 'var(--t2)' } }}
            />
          </ListItemButton>
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/signup'); }}>
            <ListItemText
              primary="Get Started"
              sx={{ '& .MuiListItemText-primary': { color: 'var(--teal)', fontWeight: 600 } }}
            />
          </ListItemButton>
        </List>
      </Drawer>

      {/* ============================================================ */}
      {/*  HERO                                                         */}
      {/* ============================================================ */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          pt: 8,
        }}
      >
        {/* Animated gradient orbs */}
        <Box
          sx={{
            position: 'absolute',
            top: '-10%',
            left: '-5%',
            width: { xs: 300, md: 520 },
            height: { xs: 300, md: 520 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,229,195,0.13) 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'landingOrb1 20s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-10%',
            right: '-5%',
            width: { xs: 260, md: 460 },
            height: { xs: 260, md: 460 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,180,216,0.11) 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'landingOrb2 25s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '60%',
            width: { xs: 200, md: 360 },
            height: { xs: 200, md: 360 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(52,237,179,0.06) 0%, transparent 70%)',
            filter: 'blur(80px)',
            animation: 'landingOrb1 30s ease-in-out infinite reverse',
            pointerEvents: 'none',
          }}
        />

        {/* Dot grid */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            pointerEvents: 'none',
          }}
        />

        {/* Hero content */}
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography
            sx={{
              ...sectionLabelSx,
              fontSize: '0.85rem',
              letterSpacing: '0.35em',
              mb: 2,
              animation: 'landingFadeInUp 0.8s ease-out both',
            }}
          >
            {brandName}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              fontSize: { xs: '2.1rem', sm: '2.8rem', md: '3.6rem' },
              lineHeight: 1.08,
              color: 'var(--t1)',
              mb: 2.5,
              letterSpacing: '-0.03em',
              animation: 'landingFadeInUp 0.8s ease-out 0.1s both',
              '& span': gradientTextSx,
            }}
          >
            Turn Audio &amp; Documents
            <br />
            into <span>Structured Text</span>
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '1rem', md: '1.15rem' },
              color: 'var(--t2)',
              maxWidth: 560,
              mx: 'auto',
              lineHeight: 1.65,
              fontWeight: 300,
              mb: 4.5,
              animation: 'landingFadeInUp 0.8s ease-out 0.2s both',
            }}
          >
            Upload a file, record your voice, or extract and chat with any document.
            Powered by advanced AI that transcribes, transforms, and structures your
            content instantly.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
              animation: 'landingFadeInUp 0.8s ease-out 0.3s both',
            }}
          >
            <Button
              variant="contained"
              onClick={() => navigate('/signup')}
              endIcon={<ArrowRight size={18} />}
              sx={{
                background: 'linear-gradient(135deg, var(--teal), var(--cyan))',
                color: 'var(--bg)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                px: 4,
                py: 1.5,
                borderRadius: 'var(--radius)',
                boxShadow: '0 4px 24px rgba(0,229,195,0.22)',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(0,229,195,0.35)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.25s ease',
              }}
            >
              Get Started Free
            </Button>
            <Button
              variant="outlined"
              onClick={() => scrollTo(NavSection.HOW_IT_WORKS)}
              sx={{
                color: 'var(--t2)',
                borderColor: 'var(--border-hi)',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '1rem',
                px: 4,
                py: 1.5,
                borderRadius: 'var(--radius)',
                '&:hover': {
                  borderColor: 'var(--teal)',
                  color: 'var(--teal)',
                  bgcolor: 'var(--teal-dim)',
                },
                transition: 'all 0.25s ease',
              }}
            >
              See How It Works
            </Button>
          </Box>
        </Container>

        {/* Scroll indicator */}
        <Box
          onClick={() => scrollTo(NavSection.FEATURES)}
          sx={{
            position: 'absolute',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            animation: 'landingBounce 2s ease-in-out infinite',
            color: 'var(--t3)',
            cursor: 'pointer',
            opacity: 0.7,
            '&:hover': { opacity: 1, color: 'var(--teal)' },
            transition: 'opacity 0.2s, color 0.2s',
          }}
        >
          <ChevronDown size={28} />
        </Box>
      </Box>

      {/* ============================================================ */}
      {/*  SOCIAL PROOF                                                 */}
      {/* ============================================================ */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          borderTop: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            data-aos="fade-up"
            sx={{
              textAlign: 'center',
              color: 'var(--t3)',
              fontSize: '0.85rem',
              fontWeight: 500,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              mb: 4,
            }}
          >
            Trusted by professionals worldwide
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: { xs: 2, md: 3 },
            }}
          >
            {STATS.map((stat, i) => (
              <Box
                key={stat.label}
                data-aos="fade-up"
                data-aos-delay={String(i * 100)}
                sx={{
                  textAlign: 'center',
                  p: 3,
                  ...glassCardSx,
                  borderRadius: 'var(--radius)',
                  transition: 'border-color 0.3s',
                  '&:hover': { borderColor: 'rgba(0,229,195,0.18)' },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Syne', sans-serif",
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    fontWeight: 700,
                    ...gradientTextSx,
                    mb: 0.5,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography sx={{ color: 'var(--t2)', fontSize: '0.85rem' }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ============================================================ */}
      {/*  FEATURES                                                     */}
      {/* ============================================================ */}
      <Box id={NavSection.FEATURES} sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }} data-aos="fade-up">
            <Typography sx={sectionLabelSx}>Features</Typography>
            <Typography
              variant="h3"
              sx={{ ...sectionHeadingSx, fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 1.5 }}
            >
              Everything you need to work
              <br />
              with audio &amp; documents
            </Typography>
            <Typography sx={{ color: 'var(--t2)', fontSize: '1rem', maxWidth: 520, mx: 'auto' }}>
              Six powerful tools in one workspace — from transcription to form filling.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Box
                  key={f.title}
                  data-aos="fade-up"
                  data-aos-delay={String(i * 80)}
                  sx={{
                    ...glassCardSx,
                    p: { xs: 3, md: 3.5 },
                    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                    '&:hover': {
                      borderColor: 'rgba(0,229,195,0.25)',
                      boxShadow: '0 8px 40px rgba(0,229,195,0.08)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 'var(--radius-sm)',
                      background: 'linear-gradient(135deg, var(--teal), var(--cyan))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                      boxShadow: '0 4px 20px rgba(0,229,195,0.25)',
                    }}
                  >
                    <Icon size={26} color="var(--bg)" strokeWidth={1.8} />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Syne', sans-serif",
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      color: 'var(--t1)',
                      mb: 1,
                    }}
                  >
                    {f.title}
                  </Typography>
                  <Typography sx={{ color: 'var(--t2)', fontSize: '0.9rem', lineHeight: 1.6 }}>
                    {f.description}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* ============================================================ */}
      {/*  HOW IT WORKS                                                 */}
      {/* ============================================================ */}
      <Box
        id={NavSection.HOW_IT_WORKS}
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(180deg, transparent, rgba(0,229,195,0.02), transparent)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }} data-aos="fade-up">
            <Typography sx={sectionLabelSx}>How It Works</Typography>
            <Typography
              variant="h3"
              sx={{ ...sectionHeadingSx, fontSize: { xs: '1.8rem', md: '2.5rem' } }}
            >
              Three simple steps
            </Typography>
          </Box>

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 5, md: 4 },
              alignItems: { md: 'flex-start' },
              justifyContent: 'center',
            }}
          >
            {/* Connecting line */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: 44,
                left: '20%',
                right: '20%',
                height: '2px',
                background: 'linear-gradient(90deg, var(--teal), var(--cyan), var(--teal))',
                opacity: 0.25,
                zIndex: 0,
              }}
            />

            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <Box
                  key={step.title}
                  data-aos="fade-up"
                  data-aos-delay={String(i * 150)}
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                    maxWidth: { md: 280 },
                  }}
                >
                  <Box
                    sx={{
                      width: 88,
                      height: 88,
                      borderRadius: '50%',
                      background: 'var(--s1)',
                      border: '2px solid var(--border-hi)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      position: 'relative',
                    }}
                  >
                    <Icon size={36} color="var(--teal)" />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, var(--teal), var(--cyan))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: 'var(--bg)',
                      }}
                    >
                      {i + 1}
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Syne', sans-serif",
                      fontWeight: 600,
                      fontSize: '1.15rem',
                      color: 'var(--t1)',
                      mb: 1,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography sx={{ color: 'var(--t2)', fontSize: '0.9rem', lineHeight: 1.6, px: 2 }}>
                    {step.description}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* ============================================================ */}
      {/*  APP PREVIEW                                                  */}
      {/* ============================================================ */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }} data-aos="fade-up">
            <Typography sx={sectionLabelSx}>See It In Action</Typography>
            <Typography
              variant="h3"
              sx={{ ...sectionHeadingSx, fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 1.5 }}
            >
              A powerful workspace for all your content
            </Typography>
          </Box>

          <Box
            data-aos="fade-up"
            data-aos-delay="100"
            sx={{
              ...glassCardSx,
              maxWidth: 920,
              mx: 'auto',
              overflow: 'hidden',
            }}
          >
            {/* Window chrome */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderBottom: '1px solid var(--border)',
              }}
            >
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FF5E5E' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#F5A623' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#22C980' }} />
              <Typography sx={{ ml: 2, fontSize: '0.75rem', color: 'var(--t3)', fontWeight: 500 }}>
                {brandName}
              </Typography>
            </Box>

            {/* Tab bar */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                p: 1,
                bgcolor: 'var(--s1)',
                borderBottom: '1px solid var(--border)',
                overflowX: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {MOCK_TABS.map((tab, i) => (
                <Box
                  key={tab}
                  sx={{
                    px: { xs: 1.5, md: 2 },
                    py: 0.75,
                    borderRadius: 'var(--radius-sm)',
                    fontSize: { xs: '0.7rem', md: '0.78rem' },
                    fontWeight: i === 0 ? 600 : 400,
                    background: i === 0 ? 'linear-gradient(135deg, var(--teal), var(--cyan))' : 'transparent',
                    color: i === 0 ? 'var(--bg)' : 'var(--t3)',
                    whiteSpace: 'nowrap',
                    cursor: 'default',
                  }}
                >
                  {tab}
                </Box>
              ))}
            </Box>

            {/* Mock content area */}
            <Box
              sx={{
                p: { xs: 2, md: 4 },
                minHeight: { xs: 260, md: 300 },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
              }}
            >
              {/* Upload zone */}
              <Box
                sx={{
                  flex: 1,
                  border: '2px dashed var(--border-hi)',
                  borderRadius: 'var(--radius)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  p: 4,
                  minHeight: { xs: 160, md: 200 },
                }}
              >
                <Upload size={40} color="var(--t3)" />
                <Typography sx={{ color: 'var(--t2)', fontSize: '0.9rem', textAlign: 'center' }}>
                  Drop audio or documents here
                </Typography>
                <Box
                  sx={{
                    px: 2.5,
                    py: 0.8,
                    borderRadius: 'var(--radius-sm)',
                    background: 'linear-gradient(135deg, var(--teal), var(--cyan))',
                    color: 'var(--bg)',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  }}
                >
                  Browse Files
                </Box>
              </Box>

              {/* Result area */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'var(--s1)',
                    borderRadius: 'var(--radius-sm)',
                    p: 2.5,
                    flex: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Sparkles size={16} color="var(--teal)" />
                    <Typography sx={{ fontSize: '0.78rem', color: 'var(--teal)', fontWeight: 600 }}>
                      AI Output
                    </Typography>
                  </Box>
                  {[85, 100, 70, 92, 58, 80, 45].map((w, i) => (
                    <Box
                      key={i}
                      sx={{
                        height: 8,
                        width: `${w}%`,
                        bgcolor: i < 2 ? 'var(--s3)' : 'var(--s2)',
                        borderRadius: 4,
                        mb: 1.2,
                      }}
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {['Copy', 'Download', 'Email'].map((a) => (
                    <Box
                      key={a}
                      sx={{
                        px: 2,
                        py: 0.6,
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border-hi)',
                        fontSize: '0.72rem',
                        color: 'var(--t2)',
                        cursor: 'default',
                      }}
                    >
                      {a}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* PRICING section hidden — re-enable when pricing is finalized */}

      {/* ============================================================ */}
      {/*  FINAL CTA                                                    */}
      {/* ============================================================ */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Box
            data-aos="fade-up"
            sx={{
              ...glassCardSx,
              p: { xs: 4, md: 8 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(0,229,195,0.06), rgba(0,180,216,0.04))',
            }}
          >
            <Typography
              variant="h3"
              sx={{ ...sectionHeadingSx, fontSize: { xs: '1.6rem', md: '2.2rem' }, mb: 2 }}
            >
              Ready to transform your workflow?
            </Typography>
            <Typography
              sx={{ color: 'var(--t2)', fontSize: '1rem', mb: 4, maxWidth: 460, mx: 'auto' }}
            >
              Join thousands of professionals using AI-Scribe to save hours every week.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/signup')}
              endIcon={<ArrowRight size={18} />}
              sx={{
                background: 'linear-gradient(135deg, var(--teal), var(--cyan))',
                color: 'var(--bg)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1.05rem',
                px: 5,
                py: 1.5,
                borderRadius: 'var(--radius)',
                boxShadow: '0 4px 24px rgba(0,229,195,0.22)',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(0,229,195,0.35)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.25s ease',
              }}
            >
              Get Started Free
            </Button>
            <Typography sx={{ color: 'var(--t3)', fontSize: '0.85rem', mt: 2 }}>
              No credit card required
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ============================================================ */}
      {/*  FOOTER                                                       */}
      {/* ============================================================ */}
      <Box
        component="footer"
        sx={{ py: { xs: 4, md: 6 }, borderTop: '1px solid var(--border)' }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 4,
            }}
          >
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  mb: 1,
                }}
              >
                <Box
                  component="img"
                  src={logoUrl}
                  alt={brandName}
                  sx={{ height: 28, width: 'auto', borderRadius: '4px' }}
                />
                <Typography
                  sx={{
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    ...gradientTextSx,
                    letterSpacing: '0.15em',
                  }}
                >
                  {brandName}
                </Typography>
              </Box>
              <Typography sx={{ color: 'var(--t3)', fontSize: '0.8rem' }}>
                Turn audio &amp; documents into structured text.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: { xs: 4, md: 6 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: 'var(--t2)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  Product
                </Typography>
                {NAV_LINKS.map((l) => (
                  <Typography
                    key={l.section}
                    onClick={() => scrollTo(l.section)}
                    sx={{
                      fontSize: '0.85rem',
                      color: 'var(--t3)',
                      cursor: 'pointer',
                      '&:hover': { color: 'var(--teal)' },
                      transition: 'color 0.2s',
                    }}
                  >
                    {l.label}
                  </Typography>
                ))}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: 'var(--t2)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  Account
                </Typography>
                <Typography
                  onClick={() => navigate('/login')}
                  sx={{
                    fontSize: '0.85rem',
                    color: 'var(--t3)',
                    cursor: 'pointer',
                    '&:hover': { color: 'var(--teal)' },
                    transition: 'color 0.2s',
                  }}
                >
                  Sign In
                </Typography>
                <Typography
                  onClick={() => navigate('/signup')}
                  sx={{
                    fontSize: '0.85rem',
                    color: 'var(--t3)',
                    cursor: 'pointer',
                    '&:hover': { color: 'var(--teal)' },
                    transition: 'color 0.2s',
                  }}
                >
                  Sign Up
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: 'var(--t2)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  Legal
                </Typography>
                <Typography
                  onClick={() => navigate('/terms')}
                  sx={{
                    fontSize: '0.85rem',
                    color: 'var(--t3)',
                    cursor: 'pointer',
                    '&:hover': { color: 'var(--teal)' },
                    transition: 'color 0.2s',
                  }}
                >
                  Terms of Service
                </Typography>
                <Typography
                  onClick={() => navigate('/privacy')}
                  sx={{
                    fontSize: '0.85rem',
                    color: 'var(--t3)',
                    cursor: 'pointer',
                    '&:hover': { color: 'var(--teal)' },
                    transition: 'color 0.2s',
                  }}
                >
                  Privacy Policy
                </Typography>
                <Typography
                  onClick={() => navigate('/refund')}
                  sx={{
                    fontSize: '0.85rem',
                    color: 'var(--t3)',
                    cursor: 'pointer',
                    '&:hover': { color: 'var(--teal)' },
                    transition: 'color 0.2s',
                  }}
                >
                  Refund Policy
                </Typography>
              </Box>

              {config?.accountManager?.email && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: 'var(--t2)',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Support
                  </Typography>
                  <Typography
                    component="a"
                    href={`mailto:${config.accountManager.email}`}
                    sx={{
                      fontSize: '0.85rem',
                      color: 'var(--t3)',
                      textDecoration: 'none',
                      '&:hover': { color: 'var(--teal)' },
                      transition: 'color 0.2s',
                    }}
                  >
                    {config.accountManager.email}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              mt: { xs: 4, md: 6 },
              pt: 3,
              borderTop: '1px solid var(--border)',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: 'var(--t4)', fontSize: '0.78rem' }}>
              &copy; {new Date().getFullYear()} {brandName}. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
